import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  DatePicker,
  message,
  Space,
  Tag,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Tooltip,
  Divider,
  Alert,
  Typography
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  DownloadOutlined,
  ExportOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import api from '../../services/api';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

interface ActivationCode {
  id: number;
  code: string;
  game_type_id: number;
  game_type_name: string;
  game_type_key: string;
  max_players: number;
  billing_period: string;
  node_id: number;
  node_name: string;
  mod_support_enabled: boolean;
  is_used: boolean;
  used_by_user_id?: number;
  used_by_username?: string;
  used_at?: string;
  created_at: string;
  created_by_admin_id: number;
  created_by_username: string;
  expires_at?: string;
  notes?: string;
}

interface GameType {
  id: number;
  display_name: string;
  name: string;
}

interface Node {
  id: number;
  name: string;
  host: string;
}

interface PricingOption {
  max_players: number;
  billing_period: string;
  price: number;
}

const ActivationCodes: React.FC = () => {
  const [activationCodes, setActivationCodes] = useState<ActivationCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    game_type_id: undefined,
    is_used: undefined,
    search: ''
  });

  // 创建激活码相关状态
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [gameTypes, setGameTypes] = useState<GameType[]>([]);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [pricingOptions, setPricingOptions] = useState<PricingOption[]>([]);
  const [selectedGameType, setSelectedGameType] = useState<number>();
  const [selectedMaxPlayers, setSelectedMaxPlayers] = useState<number>();
  const [createLoading, setCreateLoading] = useState(false);

  // 详情模态框
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedCode, setSelectedCode] = useState<ActivationCode | null>(null);

  // 导出相关状态
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportForm] = Form.useForm();
  const [exportLoading, setExportLoading] = useState(false);
  const [exportFormValues, setExportFormValues] = useState<any>({});
  
  // 预览相关状态
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewCodes, setPreviewCodes] = useState<ActivationCode[]>([]);
  
  // 显示所有激活码相关状态
  const [allCodesModalVisible, setAllCodesModalVisible] = useState(false);
  const [allCodesText, setAllCodesText] = useState('');
  
  // 生成后显示激活码相关状态
  const [generatedCodesModalVisible, setGeneratedCodesModalVisible] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState<string[]>([]);
  const [generatedCodesInfo, setGeneratedCodesInfo] = useState<any>(null);
  
  // 生成动态后缀文本的函数
  const generateDynamicSuffixText = (formValues: any) => {
    const gameTypeName = gameTypes.find(gt => gt.id === formValues.game_type_id)?.display_name || 'ARK: Survival Evolved';
    const players = formValues.max_players || 10;
    const period = formValues.billing_period;
    const modSupport = formValues.mod_support_enabled === 1;
    
    // 付费周期映射
    const periodMap: { [key: string]: string } = {
      '1D': '1天',
      '7D': '7天', 
      '30D': '30天',
      '90D': '90天',
      'weekly': '7天',
      'monthly': '30天',
      'quarterly': '90天',
      'semiannual': '180天',
      'yearly': '365天'
    };
    
    const periodText = periodMap[period] || period || '月付';
    const modText = modSupport ? 'MOD服' : '纯净服';
    
    return `    ${gameTypeName}-${players}人-${periodText}${modText}，请注册登录自助开通，创建服务器时选择激活码创建，祝您游戏愉快，万事顺遂，悠悠鸟游戏：https://game.uun.cc`;
  };

  // 统计数据
  const [stats, setStats] = useState({
    total: 0,
    used: 0,
    unused: 0,
    expired: 0
  });

  // 加载激活码列表
  const loadActivationCodes = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters
      };
      
      const response = await api.get('/admin/activation-codes', { params });
      
      if (response.data.success) {
        console.log('激活码数据:', response.data.data);
        setActivationCodes(response.data.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0
        }));
        
        // 计算统计数据
        const data = response.data.data || [];
        const total = response.data.pagination?.total || 0;
        const used = data.filter((item: ActivationCode) => item.is_used).length;
        const unused = data.filter((item: ActivationCode) => !item.is_used).length;
        const expired = data.filter((item: ActivationCode) => 
          item.expires_at && new Date(item.expires_at) < new Date()
        ).length;
        
        setStats({ total, used, unused, expired });
      }
    } catch (error) {
      console.error('加载激活码列表失败:', error);
      // 如果数据库表不存在或查询失败，显示空数据而不是错误
      setActivationCodes([]);
      setPagination(prev => ({ ...prev, total: 0 }));
      setStats({ total: 0, used: 0, unused: 0, expired: 0 });
      // 不显示错误消息，让用户看到"暂无数据"的友好提示
    } finally {
      setLoading(false);
    }
  };

  // 加载游戏类型
  const loadGameTypes = async () => {
    try {
      const response = await api.get('/admin/activation-codes/config/game-types');
      if (response.data.success) {
        setGameTypes(response.data.data);
      }
    } catch (error) {
      message.error('加载游戏类型失败');
    }
  };

  // 加载服务器节点
  const loadNodes = async () => {
    try {
      const response = await api.get('/admin/activation-codes/config/nodes');
      if (response.data.success) {
        setNodes(response.data.data);
      }
    } catch (error) {
      message.error('加载服务器节点失败');
    }
  };

  // 加载价格配置
  const loadPricingOptions = async (gameTypeId: number) => {
    try {
      const response = await api.get(`/admin/activation-codes/config/pricing/${gameTypeId}`);
      if (response.data.success) {
        setPricingOptions(response.data.data);
      }
    } catch (error) {
      message.error('加载价格配置失败');
    }
  };

  // 创建激活码
  const handleCreate = async (values: any) => {
    setCreateLoading(true);
    try {
      const response = await api.post('/admin/activation-codes', values);
      
      if (response.data.success) {
        message.success(response.data.message);
        setCreateModalVisible(false);
        createForm.resetFields();
        setSelectedGameType(undefined);
        setSelectedMaxPlayers(undefined);
        setPricingOptions([]);
        
        // 显示生成的激活码
        if (response.data.data && response.data.data.codes && response.data.data.codes.length > 0) {
          setGeneratedCodes(response.data.data.codes);
          setGeneratedCodesInfo({
            gameTypeName: response.data.data.game_type || gameTypes.find(gt => gt.id === values.game_type_id)?.display_name || 'ARK: Survival Evolved',
            maxPlayers: response.data.data.max_players || values.max_players,
            billingPeriod: response.data.data.billing_period || values.billing_period,
            modSupport: response.data.data.mod_support_enabled || values.mod_support_enabled
          });
          setGeneratedCodesModalVisible(true);
        }
        
        loadActivationCodes();
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || '创建激活码失败');
    } finally {
      setCreateLoading(false);
    }
  };

  // 删除激活码
  const handleDelete = async (id: number) => {
    try {
      const response = await api.delete(`/admin/activation-codes/${id}`);
      
      if (response.data.success) {
        message.success('删除成功');
        loadActivationCodes();
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  // 批量删除
  const handleBatchDelete = async (ids: number[]) => {
    try {
      const response = await api.delete('/admin/activation-codes/batch', {
        data: { ids }
      });
      
      if (response.data.success) {
        message.success(response.data.message);
        loadActivationCodes();
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || '批量删除失败');
    }
  };

  // 复制激活码
  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      message.success('激活码已复制到剪贴板');
    });
  };

  // 按类型导出激活码
  const handleExportByType = (record: ActivationCode) => {
    // 生成动态后缀文本
    const gameTypeName = record.game_type_name || 'ARK: Survival Evolved';
    const players = record.max_players;
    const period = record.billing_period;
    const modSupport = record.mod_support_enabled;
    
    // 付费周期映射
    const periodMap: { [key: string]: string } = {
      '1D': '1天',
      '7D': '7天', 
      '30D': '30天',
      '90D': '90天',
      'weekly': '7天',
      'monthly': '30天',
      'quarterly': '90天',
      'semiannual': '180天',
      'yearly': '365天'
    };
    
    const periodText = periodMap[period] || period;
    const modText = modSupport ? 'MOD服' : '纯净服';
    
    const dynamicSuffixText = `    ${gameTypeName}-${players}人-${periodText}${modText}，请注册登录自助开通，创建服务器时选择激活码创建，祝您游戏愉快，万事顺遂，悠悠鸟游戏：https://game.uun.cc`;
    
    exportForm.setFieldsValue({
      game_type_id: record.game_type_id,
      max_players: record.max_players,
      billing_period: record.billing_period,
      node_id: record.node_id,
      mod_support_enabled: record.mod_support_enabled ? 1 : 0,
      prefix_text: '您的激活码：',
      suffix_text: dynamicSuffixText
    });
    setExportModalVisible(true);
  };

  // 批量导出激活码
  const handleExport = async (values: any) => {
    setExportLoading(true);
    try {
      const response = await api.post('/admin/activation-codes/export', values, {
        responseType: 'blob'
      });
      
      // 创建下载链接
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `activation-codes-${Date.now()}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success('激活码导出成功');
      setExportModalVisible(false);
    } catch (error: any) {
      message.error(error.response?.data?.message || '导出失败');
    } finally {
      setExportLoading(false);
    }
  };

  // 显示所有激活码
  const handleShowAllCodes = () => {
    const codesText = activationCodes.map(code => code.code).join('\n');
    setAllCodesText(codesText);
    setAllCodesModalVisible(true);
  };

  // 导出所有激活码为TXT
  const handleExportAllCodes = () => {
    if (activationCodes.length === 0) {
      message.warning('没有激活码可导出');
      return;
    }

    // 获取第一个激活码的信息用于命名
    const firstCode = activationCodes[0];
    const gameTypeName = firstCode.game_type_name || 'ARK';
    const players = firstCode.max_players;
    const period = firstCode.billing_period;
    const modSupport = firstCode.mod_support_enabled ? 'MOD' : '纯净';
    
    // 付费周期映射
    const periodMap: { [key: string]: string } = {
      '1D': '1天',
      '7D': '7天', 
      '30D': '30天',
      '90D': '90天',
      'weekly': '7天',
      'monthly': '30天',
      'quarterly': '90天',
      'semiannual': '180天',
      'yearly': '365天'
    };
    
    const periodText = periodMap[period] || period || '月付';
    
    // 生成文件名
    const fileName = `${gameTypeName}+${periodText}+${players}人+${modSupport}.txt`;
    
    // 创建文本内容
    const content = activationCodes.map(code => code.code).join('\n');
    
    // 创建下载
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    message.success('激活码导出成功');
  };

  // 导出生成的激活码为TXT
  const handleExportGeneratedCodes = () => {
    if (generatedCodes.length === 0) {
      message.warning('没有激活码可导出');
      return;
    }

    if (!generatedCodesInfo) {
      message.error('激活码信息不完整');
      return;
    }

    const { gameTypeName, maxPlayers, billingPeriod, modSupport } = generatedCodesInfo;
    
    // 付费周期映射
    const periodMap: { [key: string]: string } = {
      '1D': '1天',
      '7D': '7天', 
      '30D': '30天',
      '90D': '90天',
      'weekly': '7天',
      'monthly': '30天',
      'quarterly': '90天',
      'semiannual': '180天',
      'yearly': '365天'
    };
    
    const periodText = periodMap[billingPeriod] || billingPeriod || '月付';
    const modSupportText = modSupport ? 'MOD' : '纯净';
    
    // 生成文件名
    const fileName = `${gameTypeName}+${periodText}+${maxPlayers}人+${modSupportText}.txt`;
    
    // 创建文本内容
    const content = generatedCodes.join('\n');
    
    // 创建下载
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    message.success('激活码导出成功');
  };

  // 查看详情
  const handleViewDetail = (record: ActivationCode) => {
    setSelectedCode(record);
    setDetailModalVisible(true);
  };

  // 表格列定义
  const columns: ColumnsType<ActivationCode> = [
    {
      title: '激活码',
      dataIndex: 'code',
      key: 'code',
      width: 200,
      render: (code: string) => (
        <Space>
          <code style={{ fontFamily: 'monospace' }}>{code}</code>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(code)}
          />
        </Space>
      )
    },
    {
      title: '游戏类型',
      dataIndex: 'game_type_name',
      key: 'game_type_name',
      width: 150
    },
    {
      title: '最大玩家数',
      dataIndex: 'max_players',
      key: 'max_players',
      width: 100,
      align: 'center'
    },
    {
      title: '付费周期',
      dataIndex: 'billing_period',
      key: 'billing_period',
      width: 100,
      render: (period: string) => {
        const periodMap: { [key: string]: string } = {
          'weekly': '周付',
          'monthly': '月付',
          'quarterly': '季付',
          'semiannual': '半年付',
          'yearly': '年付'
        };
        return periodMap[period] || period;
      }
    },
    {
      title: '服务器节点',
      dataIndex: 'node_name',
      key: 'node_name',
      width: 120
    },
    {
      title: 'MOD支持',
      dataIndex: 'mod_support_enabled',
      key: 'mod_support_enabled',
      width: 80,
      align: 'center',
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'green' : 'default'}>
          {enabled ? '支持' : '不支持'}
        </Tag>
      )
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (_, record) => {
        if (record.is_used) {
          return <Tag color="blue">已使用</Tag>;
        }
        if (record.expires_at && new Date(record.expires_at) < new Date()) {
          return <Tag color="red">已过期</Tag>;
        }
        return <Tag color="green">未使用</Tag>;
      }
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="查看详情">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>
          <Tooltip title="导出同类型激活码">
            <Button
              type="text"
              size="small"
              icon={<ExportOutlined />}
              onClick={() => handleExportByType(record)}
            />
          </Tooltip>
          {!record.is_used && (
            <Popconfirm
              title="确定要删除这个激活码吗？"
              onConfirm={() => handleDelete(record.id)}
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  useEffect(() => {
    loadActivationCodes();
    loadGameTypes();
    loadNodes();
  }, [pagination.current, pagination.pageSize, filters]);

  return (
    <div>
      <Card title="服务器激活码管理" style={{ marginBottom: 16 }}>
        {/* 统计信息 */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Statistic title="总激活码" value={stats.total} />
          </Col>
          <Col span={6}>
            <Statistic title="已使用" value={stats.used} valueStyle={{ color: '#1890ff' }} />
          </Col>
          <Col span={6}>
            <Statistic title="未使用" value={stats.unused} valueStyle={{ color: '#52c41a' }} />
          </Col>
          <Col span={6}>
            <Statistic title="已过期" value={stats.expired} valueStyle={{ color: '#ff4d4f' }} />
          </Col>
        </Row>

        {/* 操作栏 */}
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Space>
              <Select
                placeholder="游戏类型"
                style={{ width: 150 }}
                allowClear
                value={filters.game_type_id}
                onChange={(value) => setFilters(prev => ({ ...prev, game_type_id: value }))}
              >
                {gameTypes.map(type => (
                  <Option key={type.id} value={type.id}>
                    {type.display_name}
                  </Option>
                ))}
              </Select>
              
              <Select
                placeholder="使用状态"
                style={{ width: 120 }}
                allowClear
                value={filters.is_used}
                onChange={(value) => setFilters(prev => ({ ...prev, is_used: value }))}
              >
                <Option value={false}>未使用</Option>
                <Option value={true}>已使用</Option>
              </Select>
              
              <Input
                placeholder="搜索激活码或游戏类型"
                style={{ width: 200 }}
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                prefix={<SearchOutlined />}
              />
              
              <Button
                icon={<ReloadOutlined />}
                onClick={loadActivationCodes}
              >
                刷新
              </Button>
            </Space>
          </Col>
          
          <Col>
            <Space>
              <Button
                icon={<EyeOutlined />}
                onClick={handleShowAllCodes}
              >
                显示所有激活码
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => setExportModalVisible(true)}
              >
                批量导出
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateModalVisible(true)}
              >
                生成激活码
              </Button>
            </Space>
          </Col>
        </Row>

        {/* 表格 */}
        <Table
          columns={columns}
          dataSource={activationCodes}
          loading={loading}
          rowKey="id"
          locale={{
            emptyText: '暂无激活码'
          }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
          }}
          onChange={(pagination) => setPagination(prev => ({
            ...prev,
            current: pagination?.current || 1,
            pageSize: pagination?.pageSize || 20
          }))}
        />
      </Card>

      {/* 创建激活码模态框 */}
      <Modal
        title="生成激活码"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
          setSelectedGameType(undefined);
          setSelectedMaxPlayers(undefined);
          setPricingOptions([]);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreate}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="game_type_id"
                label="游戏类型"
                rules={[{ required: true, message: '请选择游戏类型' }]}
              >
                <Select
                  placeholder="选择游戏类型"
                  onChange={(value) => {
                    setSelectedGameType(value);
                    loadPricingOptions(value);
                  }}
                >
                  {gameTypes.map(type => (
                    <Option key={type.id} value={type.id}>
                      {type.display_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="node_id"
                label="服务器节点"
                rules={[{ required: true, message: '请选择服务器节点' }]}
              >
                <Select placeholder="选择服务器节点">
                  {nodes.map(node => (
                    <Option key={node.id} value={node.id}>
                      {node.name} ({node.host})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="max_players"
                label="最大玩家数"
                rules={[{ required: true, message: '请选择最大玩家数' }]}
              >
                <Select 
                  placeholder="选择最大玩家数"
                  onChange={(value) => {
                    setSelectedMaxPlayers(value);
                    // 当最大玩家数改变时，清空付费周期选择
                    createForm.setFieldsValue({ billing_period: undefined });
                  }}
                >
                  {Array.from(new Set(pricingOptions.map(option => option.max_players)))
                    .sort((a, b) => a - b)
                    .map(maxPlayers => (
                      <Option key={maxPlayers} value={maxPlayers}>
                        {maxPlayers} 人
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="billing_period"
                label="付费周期"
                rules={[{ required: true, message: '请选择付费周期' }]}
              >
                <Select 
                  placeholder="选择付费周期"
                  disabled={!selectedMaxPlayers}
                >
                  {pricingOptions
                    .filter(option => option.max_players === selectedMaxPlayers)
                    .map(option => {
                      const periodMap: { [key: string]: string } = {
                        'weekly': '周付',
                        'monthly': '月付',
                        'quarterly': '季付',
                        'semiannual': '半年付',
                        'yearly': '年付'
                      };
                      return (
                        <Option key={option.billing_period} value={option.billing_period}>
                          {periodMap[option.billing_period]} - ¥{option.price}
                        </Option>
                      );
                    })}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="mod_support_enabled"
                label="MOD支持"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="count"
                label="生成数量"
                initialValue={1}
                rules={[{ required: true, message: '请输入生成数量' }]}
              >
                <InputNumber
                  min={1}
                  max={100}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="expires_at"
            label="过期时间"
          >
            <DatePicker
              style={{ width: '100%' }}
              showTime
              placeholder="选择过期时间（可选）"
            />
          </Form.Item>

          <Form.Item
            name="notes"
            label="备注"
          >
            <Input.TextArea
              rows={3}
              placeholder="输入备注信息（可选）"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createLoading}
              >
                生成激活码
              </Button>
              <Button onClick={() => setCreateModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情模态框 */}
      <Modal
        title="激活码详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={600}
      >
        {selectedCode && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <strong>激活码：</strong>
                <code style={{ fontFamily: 'monospace', marginLeft: 8 }}>
                  {selectedCode.code}
                </code>
              </Col>
              <Col span={12}>
                <strong>状态：</strong>
                {selectedCode.is_used ? (
                  <Tag color="blue">已使用</Tag>
                ) : selectedCode.expires_at && new Date(selectedCode.expires_at) < new Date() ? (
                  <Tag color="red">已过期</Tag>
                ) : (
                  <Tag color="green">未使用</Tag>
                )}
              </Col>
              <Col span={12}>
                <strong>游戏类型：</strong>
                {selectedCode.game_type_name}
              </Col>
              <Col span={12}>
                <strong>最大玩家数：</strong>
                {selectedCode.max_players} 人
              </Col>
              <Col span={12}>
                <strong>付费周期：</strong>
                {selectedCode.billing_period}
              </Col>
              <Col span={12}>
                <strong>服务器节点：</strong>
                {selectedCode.node_name}
              </Col>
              <Col span={12}>
                <strong>MOD支持：</strong>
                <Tag color={selectedCode.mod_support_enabled ? 'green' : 'default'}>
                  {selectedCode.mod_support_enabled ? '支持' : '不支持'}
                </Tag>
              </Col>
              <Col span={12}>
                <strong>创建时间：</strong>
                {dayjs(selectedCode.created_at).format('YYYY-MM-DD HH:mm:ss')}
              </Col>
              {selectedCode.expires_at && (
                <Col span={12}>
                  <strong>过期时间：</strong>
                  {dayjs(selectedCode.expires_at).format('YYYY-MM-DD HH:mm:ss')}
                </Col>
              )}
              {selectedCode.is_used && (
                <>
                  <Col span={12}>
                    <strong>使用用户：</strong>
                    {selectedCode.used_by_username}
                  </Col>
                  <Col span={12}>
                    <strong>使用时间：</strong>
                    {dayjs(selectedCode.used_at).format('YYYY-MM-DD HH:mm:ss')}
                  </Col>
                </>
              )}
              {selectedCode.notes && (
                <Col span={24}>
                  <strong>备注：</strong>
                  <div style={{ marginTop: 8 }}>
                    {selectedCode.notes}
                  </div>
                </Col>
              )}
            </Row>
          </div>
        )}
      </Modal>

      {/* 导出激活码模态框 */}
      <Modal
        title="导出激活码"
        open={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={exportForm}
          layout="vertical"
          onFinish={handleExport}
          onValuesChange={(changedValues, allValues) => {
            setExportFormValues(allValues);
            
            // 如果游戏类型、玩家数、付费周期或MOD支持发生变化，自动更新后缀文本
            if (changedValues.game_type_id || changedValues.max_players || changedValues.billing_period || changedValues.mod_support_enabled !== undefined) {
              const dynamicSuffixText = generateDynamicSuffixText(allValues);
              exportForm.setFieldsValue({ suffix_text: dynamicSuffixText });
            }
          }}
        >
          {/* 显示符合条件的激活码数量 */}
          {(() => {
            let filteredCodes = activationCodes;
            
            if (exportFormValues.game_type_id) {
              filteredCodes = filteredCodes.filter(code => code.game_type_id === exportFormValues.game_type_id);
            }
            
            if (exportFormValues.max_players) {
              filteredCodes = filteredCodes.filter(code => code.max_players === exportFormValues.max_players);
            }
            
            if (exportFormValues.billing_period) {
              filteredCodes = filteredCodes.filter(code => code.billing_period === exportFormValues.billing_period);
            }
            
            if (exportFormValues.node_id) {
              filteredCodes = filteredCodes.filter(code => code.node_id === exportFormValues.node_id);
            }
            
            if (exportFormValues.mod_support_enabled !== undefined) {
              filteredCodes = filteredCodes.filter(code => (code.mod_support_enabled ? 1 : 0) === exportFormValues.mod_support_enabled);
            }
            
            if (exportFormValues.is_used !== undefined) {
              filteredCodes = filteredCodes.filter(code => (code.is_used ? 1 : 0) === exportFormValues.is_used);
            }
            
            return (
              <Alert
                message={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>符合条件的激活码：{filteredCodes.length} 个</span>
                    {filteredCodes.length > 0 && (
                      <Button 
                        type="link" 
                        size="small"
                        onClick={() => {
                          setPreviewCodes(filteredCodes);
                          setPreviewModalVisible(true);
                        }}
                      >
                        查看列表
                      </Button>
                    )}
                  </div>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            );
          })()}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="game_type_id"
                label="游戏类型"
                rules={[{ required: true, message: '请选择游戏类型' }]}
              >
                <Select placeholder="选择游戏类型">
                  {gameTypes
                    .filter(type => activationCodes.some(code => code.game_type_id === type.id))
                    .map(type => (
                      <Option key={type.id} value={type.id}>
                        {type.display_name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="max_players"
                label="最大玩家数"
                rules={[{ required: true, message: '请选择最大玩家数' }]}
              >
                <Select placeholder="选择玩家数">
                  {(() => {
                    // 根据已选择的游戏类型筛选激活码
                    let filteredCodes = activationCodes;
                    
                    if (exportFormValues.game_type_id) {
                      filteredCodes = filteredCodes.filter(code => code.game_type_id === exportFormValues.game_type_id);
                    }
                    
                    // 提取唯一的玩家数并排序
                    const uniquePlayers = Array.from(new Set(filteredCodes.map(code => code.max_players)))
                      .sort((a, b) => a - b);
                    
                    return uniquePlayers.map(players => (
                      <Option key={players} value={players}>
                        {players}人
                      </Option>
                    ));
                  })()}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="billing_period"
                label="付费周期"
                rules={[{ required: true, message: '请选择付费周期' }]}
              >
                <Select placeholder="选择付费周期">
                  {(() => {
                    // 根据已选择的游戏类型和玩家数筛选激活码
                    let filteredCodes = activationCodes;
                    
                    if (exportFormValues.game_type_id) {
                      filteredCodes = filteredCodes.filter(code => code.game_type_id === exportFormValues.game_type_id);
                    }
                    
                    if (exportFormValues.max_players) {
                      filteredCodes = filteredCodes.filter(code => code.max_players === exportFormValues.max_players);
                    }
                    
                    // 提取唯一的付费周期
                    const uniquePeriods = Array.from(new Set(filteredCodes.map(code => code.billing_period)));
                    
                    const periodMap: { [key: string]: string } = {
                      '1D': '1天',
                      '7D': '7天', 
                      '30D': '30天',
                      '90D': '90天',
                      'weekly': '7天',
                      'monthly': '30天',
                      'quarterly': '90天',
                      'semiannual': '180天',
                      'yearly': '365天'
                    };
                    
                    return uniquePeriods.map(period => (
                      <Option key={period} value={period}>
                        {periodMap[period] || period}
                      </Option>
                    ));
                  })()}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="node_id"
                label="服务器节点"
                rules={[{ required: true, message: '请选择服务器节点' }]}
              >
                <Select placeholder="选择服务器节点">
                  {nodes
                    .filter(node => activationCodes.some(code => code.node_id === node.id))
                    .map(node => (
                      <Option key={node.id} value={node.id}>
                        {node.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="mod_support_enabled"
                label="MOD支持"
              >
                <Select placeholder="选择MOD支持" allowClear>
                  <Option value={0}>不支持MOD</Option>
                  <Option value={1}>支持MOD</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="is_used"
                label="使用状态"
              >
                <Select placeholder="选择使用状态" allowClear>
                  <Option value={0}>未使用</Option>
                  <Option value={1}>已使用</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="prefix_text"
            label="前缀文本"
            initialValue="您的激活码："
          >
            <Input placeholder="激活码前面的文本" />
          </Form.Item>

          <Form.Item
            name="suffix_text"
            label="后缀文本"
            initialValue="    ARK: Survival Evolved-10人-月付MOD服，请注册登录自助开通，创建服务器时选择激活码创建，祝您游戏愉快，万事顺遂，悠悠鸟游戏：https://game.uun.cc"
          >
            <Input.TextArea 
              rows={3} 
              placeholder="激活码后面的文本（会根据筛选条件自动生成）"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={exportLoading}>
                导出激活码
              </Button>
              <Button onClick={() => setExportModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 预览符合条件的激活码列表 */}
      <Modal
        title="符合条件的激活码列表"
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={1000}
      >
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {previewCodes.length === 0 ? (
            <Alert
              message="没有符合条件的激活码"
              description="当前筛选条件下没有找到激活码"
              type="info"
              showIcon
            />
          ) : (
            <Table
              dataSource={previewCodes}
              columns={[
                {
                  title: '激活码',
                  dataIndex: 'code',
                  key: 'code',
                  width: 200,
                  render: (text: string) => (
                    <Text code style={{ fontSize: '12px' }}>
                      {text}
                    </Text>
                  )
                },
                {
                  title: '游戏类型',
                  dataIndex: 'game_type_name',
                  key: 'game_type_name',
                  width: 120
                },
                {
                  title: '玩家数',
                  dataIndex: 'max_players',
                  key: 'max_players',
                  width: 80,
                  render: (players: number) => `${players}人`
                },
                {
                  title: '付费周期',
                  dataIndex: 'billing_period',
                  key: 'billing_period',
                  width: 100,
                  render: (period: string) => {
                    const periodMap: { [key: string]: string } = {
                      '1D': '1天',
                      '7D': '7天', 
                      '30D': '30天',
                      '90D': '90天',
                      'weekly': '7天',
                      'monthly': '30天',
                      'quarterly': '90天',
                      'semiannual': '180天',
                      'yearly': '365天'
                    };
                    return periodMap[period] || period;
                  }
                },
                {
                  title: '服务器节点',
                  dataIndex: 'node_name',
                  key: 'node_name',
                  width: 120
                },
                {
                  title: 'MOD支持',
                  dataIndex: 'mod_support_enabled',
                  key: 'mod_support_enabled',
                  width: 80,
                  render: (enabled: boolean) => (
                    <Tag color={enabled ? 'green' : 'default'}>
                      {enabled ? '支持' : '不支持'}
                    </Tag>
                  )
                },
                {
                  title: '使用状态',
                  dataIndex: 'is_used',
                  key: 'is_used',
                  width: 80,
                  render: (used: boolean) => (
                    <Tag color={used ? 'red' : 'green'}>
                      {used ? '已使用' : '未使用'}
                    </Tag>
                  )
                },
                {
                  title: '创建时间',
                  dataIndex: 'created_at',
                  key: 'created_at',
                  width: 150,
                  render: (time: string) => new Date(time).toLocaleString()
                }
              ]}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 个激活码`
              }}
              size="small"
              rowKey="id"
            />
          )}
        </div>
      </Modal>

      {/* 显示所有激活码模态框 */}
      <Modal
        title="所有激活码"
        open={allCodesModalVisible}
        onCancel={() => setAllCodesModalVisible(false)}
        footer={[
          <Button key="export" type="primary" onClick={handleExportAllCodes}>
            导出为TXT
          </Button>,
          <Button key="close" onClick={() => setAllCodesModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            message={`共 ${activationCodes.length} 个激活码`}
            type="info"
            showIcon
          />
        </div>
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {allCodesText ? (
            <Input.TextArea
              value={allCodesText}
              readOnly
              rows={20}
              style={{ 
                fontFamily: 'monospace',
                fontSize: '12px',
                lineHeight: '1.2'
              }}
              placeholder="没有激活码"
            />
          ) : (
            <Alert
              message="没有激活码"
              description="当前没有激活码可显示"
              type="info"
              showIcon
            />
          )}
        </div>
      </Modal>

      {/* 显示生成的激活码模态框 */}
      <Modal
        title="生成的激活码"
        open={generatedCodesModalVisible}
        onCancel={() => setGeneratedCodesModalVisible(false)}
        footer={[
          <Button key="export" type="primary" onClick={handleExportGeneratedCodes}>
            导出为TXT
          </Button>,
          <Button key="close" onClick={() => setGeneratedCodesModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            message={`成功生成 ${generatedCodes.length} 个激活码`}
            type="success"
            showIcon
          />
          {generatedCodesInfo && (
            <div style={{ marginTop: 8, fontSize: '14px', color: '#666' }}>
              <div>游戏类型: {generatedCodesInfo.gameTypeName}</div>
              <div>最大玩家数: {generatedCodesInfo.maxPlayers}人</div>
              <div>付费周期: {generatedCodesInfo.billingPeriod}</div>
              <div>MOD支持: {generatedCodesInfo.modSupport ? '支持' : '不支持'}</div>
            </div>
          )}
        </div>
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {generatedCodes.length > 0 ? (
            <Input.TextArea
              value={generatedCodes.join('\n')}
              readOnly
              rows={20}
              style={{ 
                fontFamily: 'monospace',
                fontSize: '12px',
                lineHeight: '1.2'
              }}
              placeholder="没有激活码"
            />
          ) : (
            <Alert
              message="没有激活码"
              description="当前没有激活码可显示"
              type="info"
              showIcon
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ActivationCodes;
